<template>
    <div class="">
        <slide-over-panel v-model:show="showEditModal">
            <template #title>
                <span class="font-semibold">{{ editingRow.Name }}</span> Snapshot Issues
            </template>
            <template #title-content>
                Any issues identified by the Alarm Snapshot feature are shown below. You can ignore these to
                stop getting alerts about points that don't need certain types of alarming, or click fix
                to start resolving the issue.
            </template>
            <template #content>
                <div>
                    <ul class="grid grid-cols-1 gap-6 sm:grid-cols-1 lg:grid-cols-2">
                        <snapshot-instance
                            v-for="snapshot in snapshotsWithIssues(editingRow)"
                            :key="snapshot.id"
                            :snapshot="snapshot"
                            @update:snapshot="updateSnapshot"
                        >
                        </snapshot-instance>
                    </ul>
                    <div v-if="snapshotsWithIssues(editingRow).length === 0" class="border-t border-gray-200">
                        <div class="-mt-px flex">
                            <div class="flex w-0 flex-1 border-gray-200">
                                <a class="relative inline-flex py-3 items-center justify-center flex-1 w-0 border-2
                                                    border-blue-200 text-md leading-5 font-md font-sans rounded-md
                                                    text-blue-700 bg-white-600 hover:bg-blue-200 focus:outline-none
                                                    focus:ring-blue-200 focus:border-blue-400 active:bg-blue-300
                                                    rounded-bl-lg cursor-pointer focus:z-10 focus:no-underline"
                                   :href="meterRoute(editingRow)"
                                   target="_blank">
                                    <span class="pr-2 lnr icon-cake"></span>
                                    Everything's Enabled! View the Point.
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </slide-over-panel>

        <div class="flex flex-col justify-between sm:flex-row">
            <inline-tab-header
                :headers="filterHeaders"
                name="Filter By"
                :current="activeHeaderFilter"
                @update:current="updateCurrentFilter"
            ></inline-tab-header>
            <div class="my-2 ml-0 w-full sm:m-2 sm:ml-2 sm:w-1/5">
                <select2-group :options='areaZones'
                               :close-on-select="true"
                               :allow-clear="true"
                               :multiple="false"
                               v-model='zone'
                               @input="this.loadData"
                               placeholder="Filter by Zone"
                               class="m-2 mt-4 p-2"
                ></select2-group>
            </div>
        </div>

        <ajax-table v-model:loading="ajaxTable.loading"
                    v-model:search="ajaxTable.search"
                    v-model:sort-by="ajaxTable.sortBy"
                    v-model:sort-desc="ajaxTable.sortDesc"
                    v-model:page="ajaxTable.page"
                    v-model:items-per-page="ajaxTable.itemsPerPage"
                    v-model:page-info="ajaxTable.pageInfo"
                    :data="tableData"
                    :headers="tableHeaders"
                    :exportable="false"
                    @loadData="loadData"
                    no-data-message="No alarm snapshots found"
                    loading-message="Loading Alarm Snapshots"
        >

            <!-- Snapshot Issue Column -->
            <template #col-1="props">
                <div class="flex flex-col gap-2 text-gray-700">

                    <div class="flex gap-2">
                        <span v-if="snapshotOk(props.row)" class="inline-flex items-center rounded-md bg-green-100 px-3 text-xs font-medium text-green-800 gap-x-1.5 py-1.5">
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M5 12l5 5l10 -10"/></svg>
                            <span class="hidden sm:inline">Good</span>
                        </span>

                        <span v-if="snapshotErrors(props.row)" class="inline-flex items-center rounded-md bg-red-100 px-3 text-xs font-medium text-red-800 gap-x-1.5 py-1.5">
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M18 6l-12 12"/><path d="M6 6l12 12"/></svg>
                            <span class="hidden sm:inline">Issue</span>
                        </span>

                        <span v-if="snapshotErrorsIgnored(props.row)" class="inline-flex items-center rounded-md bg-yellow-100 px-3 text-xs font-medium text-yellow-800 gap-x-1.5 py-1.5">
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 9v4"/><path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z"/><path d="M12 16h.01"/></svg>
                            <span class="hidden sm:inline">Ignored</span>
                        </span>
                    </div>

                    <div class="flex flex-col space-y-2">
                        <div class="text-base">
                            <a :href="company.buildCompanyRoute('meters/'+props.row.ID)">
                                {{ props.row.Name }}
                            </a>
                        </div>

                        <div class="ml-0 flex flex-col gap-1 sm:ml-2">
                            <div class="text-sm text-gray-500">
                                {{ props.row.zone.area.Name }}
                            </div>

                            <div class="text-sm text-gray-500">
                                {{ props.row.zone.Name }}
                            </div>
                        </div>
                    </div>
                </div>

            </template>

            <template #col-2="props">
                <div class="flex flex-col gap-2 text-gray-700 sm:self-end">

                    <div class="text-base">
                        <a v-if="alarmProfileLink(props.row)" :href="alarmProfileLink(props.row)">
                            {{ alarmProfileName(props.row) }}
                        </a>

                        <p v-else class="m-0 p-0"> {{ alarmProfileName(props.row) }}</p>
                    </div>


                    <!-- Alarm States Grid -->
                    <div class="flex flex-col gap-1">
                        <!-- Threshold Row -->
                        <div class="ml-0 flex flex-col justify-start gap-1 sm:ml-2 sm:flex-row sm:items-center">
                            <span class="w-20 text-sm text-gray-500">Threshold</span>

                            <div class="ml-1 flex items-center gap-2 sm:ml-0">
                                <!-- Threshold Alarms -->
                                <span :class="alarmProfileThreshold(props.row) ? 'text-green-500' : 'text-red-500'"
                                      :title="'Threshold Alarms ' + (alarmProfileThreshold(props.row) ? 'Enabled' : 'Disabled')">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M10 5a2 2 0 1 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3h-16a4 4 0 0 0 2 -3v-3a7 7 0 0 1 4 -6"/><path d="M9 17v1a3 3 0 0 0 6 0v-1"/></svg>
                                </span>

                                <!-- Threshold Auto-Resolve -->
                                <span :class="alarmProfileThreshold(props.row) && alarmProfileThresholdResolve(props.row) ? 'text-green-500' : 'text-gray-300'"
                                      :title="'Threshold Auto-Resolve ' + (alarmProfileThreshold(props.row) && alarmProfileThresholdResolve(props.row) ? 'Enabled' : 'Disabled')">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4"/><path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4"/></svg>
                                </span>
                            </div>

                        </div>

                        <!-- Offline Row -->
                        <div class="ml-0 flex flex-col justify-start gap-1 sm:ml-2 sm:flex-row sm:items-center">
                            <span class="w-20 text-sm text-gray-500">Offline</span>

                            <div class="ml-1 flex items-center gap-2 sm:ml-0">
                                <!-- Offline Alarms -->
                                <span :class="alarmProfileOffline(props.row) ? 'text-green-500' : 'text-red-500'"
                                      :title="'Offline Alarms ' + (alarmProfileOffline(props.row) ? 'Enabled' : 'Disabled')">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M10 5a2 2 0 1 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3h-16a4 4 0 0 0 2 -3v-3a7 7 0 0 1 4 -6"/><path d="M9 17v1a3 3 0 0 0 6 0v-1"/></svg>
                                </span>

                                <!-- Offline Auto-Resolve -->
                                <span :class="alarmProfileOffline(props.row) && alarmProfileOfflineResolve(props.row) ? 'text-green-500' : 'text-gray-300'"
                                      :title="'Offline Auto-Resolve ' + (alarmProfileOffline(props.row) && alarmProfileOfflineResolve(props.row) ? 'Enabled' : 'Disabled')">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4"/><path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4"/></svg>
                                </span>
                            </div>

                        </div>
                    </div>

                </div>
            </template>

            <!-- Alarm Thresholds Column -->
            <template #col-3="tableProps">
                <div class="flex flex-col items-center gap-1 py-2">
                    <!-- High Threshold -->
                    <div v-if="alarmProfileThresholdHigh(tableProps.row) !== false || alarmProfileCautionThresholdHigh(tableProps.row) !== false" class="flex flex-col items-center gap-0.5">
                         <span class="inline-flex items-center rounded-md bg-red-100 py-1 text-xs font-medium text-red-800 px-2.5">
                            {{alarmProfileValueString(alarmProfileThresholdHigh(tableProps.row), alarmProfileCautionThresholdHigh(tableProps.row))}}
                        </span>

                        <span class="text-red-500">
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 5l0 14"/><path d="M18 11l-6 -6"/><path d="M6 11l6 -6"/></svg>
                        </span>
                    </div>

                    <!-- Low Threshold -->
                    <div v-if="alarmProfileThresholdLow(tableProps.row) !== false || alarmProfileCautionThresholdLow(tableProps.row) !== false" class="flex flex-col items-center gap-0.5">
                        <span class="text-blue-500">
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 5l0 14"/><path d="M18 13l-6 6"/><path d="M6 13l6 6"/></svg>
                        </span>

                        <span class="inline-flex items-center rounded-md bg-blue-100 py-1 text-xs font-medium text-blue-800 px-2.5">
                            {{alarmProfileValueString(alarmProfileThresholdLow(tableProps.row), alarmProfileCautionThresholdLow(tableProps.row))}}
                        </span>
                    </div>

                    <!-- No Thresholds Set -->
                    <div v-if="alarmProfileThresholdHigh(tableProps.row) === false && alarmProfileCautionThresholdHigh(tableProps.row) === false && alarmProfileThresholdLow(tableProps.row) === false && alarmProfileCautionThresholdLow(tableProps.row) === false" class="text-sm text-gray-500">
                        No thresholds
                    </div>
                </div>
            </template>

            <template #col-4="props">
                <div class="flex w-full flex-col items-end justify-start">
                    <div class="flex flex-row gap-4">
                        <button class="transition-colors duration-150 hover:text-blue-600"
                                @click="openEditModal(props.row)"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" /><path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" /></svg>
                        </button>
                    </div>
                </div>
            </template>

        </ajax-table>
    </div>
</template>

<script>
import axios from "axios"
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from '@/mixins/IconNamesMixin';
import {ajaxTableMixin} from "@/mixins/ajaxTableMixin";
import AjaxTable from "@/components/elements/AjaxTable";
import SlideOverPanel from "@/components/elements/SlideOverPanel";
import Select2 from "@/components/elements/select2"
import ButtonBluePrimary from "@/components/elements/buttons/ButtonBluePrimary";
import ButtonGreenPrimary from "@/components/elements/buttons/ButtonGreenPrimary";
import _ from "lodash";
import UndecoratedLink from "@/components/EnterpriseDashboard/undecorated-link";
import SnapshotInstance from "@/components/SnapshotInstance";
import InlineTabHeader from "@/components/elements/InlineTabHeader";
import Select2Group from "@/components/elements/select2-group";

export default {
    name: "CalibrationIndex",
    mixins: [SonicuCoreMixin, iconNamesMixin, ajaxTableMixin,],
    components: {
        UndecoratedLink, ButtonGreenPrimary, ButtonBluePrimary,
        SlideOverPanel, AjaxTable, Select2, SnapshotInstance, InlineTabHeader, Select2Group
    },
    props: {
        viewAllUrl: String,
        areaZones: Object,
    },
    data() {
        return {
            tableHeaders: [
                {
                    name: 'Snapshot Issues',
                    key: 'issue',
                    sortBy: 'snapshot',
                    show: 'sm'
                },
                {
                    name: 'Alarm Profile',
                    key: 'meters',
                    sortBy: '',
                    show: 'sm'
                },
                {
                    name: 'Alarm Thresholds',
                    key: 'enabled',
                    sortBy: '',
                    show: 'md'
                },
                {
                    name: '',
                    key: 'enabled',
                    sortBy: '',
                    show: 'sm',

                }
            ],
            tableData: [],
            allowedPoints: [],

            // Editing Modal
            showEditModal: false,
            editingRow: null,
            name: '',
            zone: null,
            showLifeTimesModal: false,
            requestNumber: 0,
            filterHeaders: [
                {
                    text: 'All Points',
                    event: 'all-points',
                },
                {
                    text: 'Active Issues',
                    event: 'active-issues',
                },
                {
                    text: 'Active & Ignored Issues',
                    event: 'all-issues',
                },
            ],
            activeHeaderFilter: null,
            showIssuesOnly: false,
            showIgnored: true,
        }
    },
    created() {
        // this.loadCompany()
    },
    mounted() {

    },
    methods: {
        loadData() {
            this.ajaxTable.loading = true
            this.ajaxTable.requestNumber++
            let params = JSON.parse(JSON.stringify(this.ajaxTable))
            params.requestNumber = this.ajaxTable.requestNumber
            params.showIssuesOnly = this.showIssuesOnly
            params.showIgnored = this.showIgnored
            if(this.zone){
                params.zone = this.zone
            }
            axios.get(this.company.buildCompanyApiInternalRoute('snapshot'), {
                params: params,
            }).then(response => {
                let data = response.data
                this.meters = []
                this.meters = data.data

                this.tableData = data.data

                this.ajaxUpdateTableData(data)
            }).catch(error => {
                console.log(error)
            }).then(() => {
                this.ajaxTable.loading = false
            })
        },
        updateSnapshot(event) {
            Object.keys(this.editingRow.snapshot).forEach(key => {
                if (this.editingRow.snapshot[key].id !== event.id) {
                    return
                }
                this.editingRow.snapshot[key] = event
            })
        },
        openEditModal(row) {
            console.log('openEditModal triggered')
            this.editingRow = row
            this.showEditModal = true
        },
        snapshotErrors(row) {
            let result = false;
            if (! _.isNil(row.snapshot)) {
                row.snapshot.forEach((snapshot, index) => {
                    if (Boolean(snapshot.error) === true) {
                        result = true;
                    }
                })
            }
            return result;
        },
        snapshotErrorsIgnored(row) {
            // If there are errors, but they are ignored, then
            let result = false;
            if (! _.isNil(row.snapshot)) {
                row.snapshot.forEach((snapshot, index) => {
                    if (Boolean(snapshot.issue) === true && Boolean(snapshot.ignored) === true) {
                        result = true;
                    }
                })
            }
            return result;
        },
        snapshotOk(row) {
            return this.snapshotErrors(row) === false && this.snapshotErrorsIgnored(row) === false;
        },
        snapshotsWithIssues(row){
            return _.filter(row.snapshot, obj => {
                return Boolean(obj.issue) === true;
            })
        },
        meterRoute(row){
            return this.company.buildCompanyRoute('meters/' + row.ID);
        },
        alarmProfileLink(row) {
            if (_.isNil(row.alarm_profile)) {
                return null;
            }
            return this.company.buildCompanyRoute('alarm-profiles/' + row.alarm_profile.id);
        },
        alarmProfileName(row) {
            if (_.isNil(row.alarm_profile)) {
                return '';
            }
            return row.alarm_profile.name;
        },
        alarmProfileOffline(row) {
            if (_.isNil(row.alarm_profile)) {
                return false;
            }
            return row.alarm_profile.offline_alarms_enabled;
        },
        alarmProfileOfflineResolve(row) {
            if (_.isNil(row.alarm_profile)) {
                return false;
            }
            return row.alarm_profile.offline_resolve_enabled;
        },
        alarmProfileThresholdResolve(row) {
            if (_.isNil(row.alarm_profile)) {
                return false;
            }
            return row.alarm_profile.threshold_resolve_enabled;
        },
        alarmProfileThreshold(row) {
            if (_.isNil(row.alarm_profile)) {
                return false;
            }
            return row.alarm_profile.threshold_alarms_enabled;
        },
        alarmProfileThresholdHigh(row) {
            if (_.isNil(row.alarm_profile)) {
                return false;
            }
            if(_.isNil(row.alarm_profile.high_alarm_time) && row.alarm_profile.high_alarm_instant === false){
                return false;
            }
            return _.isNil(row.alarm_profile.high_alarm_value) ? false : row.alarm_profile.high_alarm_value;
        },
        alarmProfileThresholdLow(row) {
            if (_.isNil(row.alarm_profile)) {
                return false;
            }
            if(_.isNil(row.alarm_profile.low_alarm_time) && row.alarm_profile.low_alarm_instant === false){
                return false;
            }
            return _.isNil(row.alarm_profile.low_alarm_value) ? false : row.alarm_profile.low_alarm_value;
        },
        alarmProfileCautionThresholdHigh(row) {
            if (_.isNil(row.alarm_profile)) {
                return false;
            }
            if(_.isNil(row.alarm_profile.high_caution_time) && row.alarm_profile.high_caution_instant === false){
                return false;
            }
            return _.isNil(row.alarm_profile.high_caution_value) ? false : row.alarm_profile.high_caution_value;
        },
        alarmProfileCautionThresholdLow(row) {
            if (_.isNil(row.alarm_profile)) {
                return false;
            }
            if(_.isNil(row.alarm_profile.low_caution_time) && row.alarm_profile.low_caution_instant === false){
                return false;
            }
            return _.isNil(row.alarm_profile.low_caution_value) ? false : row.alarm_profile.low_caution_value;
        },
        alarmProfileValueString(first, second){
            if(first === 0){
                first = first.toString();
            }
            if(second === 0){
                second = second.toString();
            }
            if(first && second){
                return first + ", " + second;
            }
            if(first){
                return first;
            }
            if(second){
                return second;
            }
            return '';
        },
        updateCurrentFilter(currentFilter) {
            this.page = 1
            this.showIssuesOnly = currentFilter === 'active-issues' ||
                currentFilter === 'all-issues'
            this.showIgnored = currentFilter === 'all-issues'
            this.activeHeaderFilter = currentFilter
            this.loadData()
        },
    },
    computed: {
        allowedPointsOptions() {
            let keyedNames = {}
            _.forEach(this.allowedPoints, function (val) {
                keyedNames[val.serial_number] = val.name;
            })
            return keyedNames;
        }
    },
    watch: {},
}
</script>

<style scoped>

</style>